"use strict";
/**
 * TESTING — FORMULE CANONICHE (@leopart/calc).
 *
 * Migrazione integrale di `apps/backend/src/utils/testing-calculations.ts`
 * (Fase 3 calc engine, censimento R7 in vault/Architettura/Calcoli e
 * formule.md): la SEMANTICA BACKEND è il canone. Il duplicato frontend
 * (174 righe, drift certo su 8 funzioni) è stato ritirato: il FE ora
 * delega a questo modulo e ADOTTA le soglie canoniche:
 *
 *  - LSI: giallo ≥85 (il FE usava ≥75 → atleti "gialli" sul web erano
 *    "rossi" per il backend);
 *  - H:Q ratio: giallo ≥0.50 (FE usava ≥0.45);
 *  - Recovery %: giallo ≥80 (FE usava ≥75);
 *  - percentile: metodo midpoint per i ties, n=1 → 100, context → 50
 *    (il FE contava solo gli strettamente inferiori, senza ties);
 *  - percentile CON normalizzazione unità a monte (vedi
 *    `normalizeTestValue`): il FE confrontava kg e lb crudi nello stesso
 *    array → percentili diversi dal BE per team con unità miste. Fix VOLUTO.
 *  - trend: soglia flat adattiva σ×0.1 (FE usava soglia fissa 0.01);
 *  - delta: colore `grey` su Δ===0 (FE usava `gray` su |Δ|<0.001);
 *  - conversioni: fattori unici da `constants.ts` (LB_TO_KG/IN_TO_CM),
 *    output arrotondato a 2 decimali.
 *
 * Zero dipendenze, funzioni pure. I wrapper (BE `utils/testing-calculations`,
 * FE `utils/testing-calculations`) ri-esportano con firme invariate.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DERIVED_METRICS = exports.GRAVITY = void 0;
exports.calculateTeamAverage = calculateTeamAverage;
exports.calculatePercentile = calculatePercentile;
exports.calculateDelta = calculateDelta;
exports.calculateBMI = calculateBMI;
exports.calculateLSI = calculateLSI;
exports.calculateHQRatio = calculateHQRatio;
exports.calculateEUR = calculateEUR;
exports.calculateTrendSlope = calculateTrendSlope;
exports.convertUnit = convertUnit;
exports.normalizeTestValue = normalizeTestValue;
exports.calculateRecoveryPct = calculateRecoveryPct;
exports.isRTPCriteriaMet = isRTPCriteriaMet;
exports.rsiFromFlightContact = rsiFromFlightContact;
exports.jumpHeightFromFlightTime = jumpHeightFromFlightTime;
exports.applyDerivedMetrics = applyDerivedMetrics;
const constants_1 = require("./constants");
// ═══════════════════════════════════════════════════════════════════
// Funzioni pure per i calcoli del modulo Testing (spec sez. 9)
// ═══════════════════════════════════════════════════════════════════
/**
 * Calcola la media squadra escludendo valori null/undefined (spec sez. 9.1).
 * Ritorna 0 se non ci sono valori validi.
 */
function calculateTeamAverage(values) {
    const valid = values.filter((v) => v != null && !isNaN(v));
    if (valid.length === 0)
        return 0;
    return valid.reduce((sum, v) => sum + v, 0) / valid.length;
}
/**
 * Calcola il percentile di un valore rispetto a un array di valori (spec sez. 9.2).
 * Usa il metodo midpoint per gestire i ties:
 *   percentile = (count_inferiori + 0.5 × count_pari) / totale × 100
 * Per metriche ↑=meglio: "inferiori" = valori strettamente minori.
 * Per metriche ↓=meglio: "inferiori" = valori strettamente maggiori.
 * Per direction 'context': ritorna 50 (neutro).
 * Ritorna il percentile arrotondato all'intero.
 *
 * NB unità: `value` e `allValues` devono essere già nella STESSA unità
 * canonica — normalizzare a monte con `normalizeTestValue` (è così che fa
 * il backend in testing.service; il FE storicamente non lo faceva → drift).
 */
function calculatePercentile(value, allValues, direction) {
    if (allValues.length === 0)
        return 0;
    if (allValues.length === 1)
        return 100;
    if (direction === 'context')
        return 50;
    const countBelow = direction === 'higher_better'
        ? allValues.filter((v) => v < value).length
        : allValues.filter((v) => v > value).length;
    const countEqual = allValues.filter((v) => v === value).length;
    return Math.round(((countBelow + 0.5 * countEqual) / allValues.length) * 100);
}
/**
 * Calcola il delta tra ultimo e precedente valore con colore (spec sez. 9.3).
 * delta = ultimo - penultimo (o primo per baseline).
 * Verde se miglioramento, rosso se peggioramento, grigio per 'context'.
 */
function calculateDelta(current, previous, direction) {
    const value = current - previous;
    if (direction === 'context') {
        return { value, color: 'grey', isImprovement: false };
    }
    const isImprovement = direction === 'higher_better' ? value > 0 : value < 0;
    return {
        value,
        color: value === 0 ? 'grey' : isImprovement ? 'green' : 'red',
        isImprovement,
    };
}
/**
 * Calcola il BMI (Body Mass Index) automaticamente (spec sez. 9.4).
 * BMI = peso_kg / (altezza_cm / 100)²
 * Ritorna null se i dati non sono validi.
 */
function calculateBMI(weightKg, heightCm) {
    if (weightKg == null || heightCm == null || heightCm <= 0 || weightKg <= 0) {
        return null;
    }
    const heightM = heightCm / 100;
    return Math.round((weightKg / (heightM * heightM)) * 100) / 100;
}
/**
 * Calcola il Limb Symmetry Index (spec sez. 9.5).
 * LSI = (lato_debole / lato_forte) × 100
 * Il lato debole è automaticamente il minore dei due valori.
 * Zone canoniche: verde ≥90 · giallo ≥85 · rosso <85 (il FE usava
 * giallo ≥75 — soglia ritirata).
 * Ritorna null se uno dei due lati manca.
 */
function calculateLSI(sideDx, sideSx) {
    if (sideDx == null || sideSx == null || sideDx <= 0 || sideSx <= 0) {
        return null;
    }
    const strong = Math.max(sideDx, sideSx);
    const weak = Math.min(sideDx, sideSx);
    const value = Math.round((weak / strong) * 1000) / 10; // 1 decimale
    let zone;
    if (value >= 90)
        zone = 'green';
    else if (value >= 85)
        zone = 'yellow';
    else
        zone = 'red';
    return { value, zone };
}
function calculateHQRatio(hamstringForce, quadForce) {
    if (hamstringForce == null || quadForce == null || quadForce <= 0) {
        return null;
    }
    const value = Math.round((hamstringForce / quadForce) * 100) / 100;
    let zone;
    if (value >= 0.6)
        zone = 'green';
    else if (value >= 0.5)
        zone = 'yellow';
    else
        zone = 'red';
    return { value, zone };
}
/**
 * Calcola l'Eccentric Utilization Ratio (spec sez. 9.7).
 * EUR = altezza_CMJ / altezza_SJ
 * Valori tipici: 1.05–1.20.
 * Ritorna null se uno dei valori manca.
 */
function calculateEUR(cmjHeight, sjHeight) {
    if (cmjHeight == null || sjHeight == null || sjHeight <= 0) {
        return null;
    }
    return Math.round((cmjHeight / sjHeight) * 100) / 100;
}
function calculateTrendSlope(values, direction) {
    if (values.length < 2) {
        return { slope: 0, trend: 'flat' };
    }
    // Regressione lineare semplice: y = a + b*x
    const n = values.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;
    for (let i = 0; i < n; i++) {
        sumX += i;
        sumY += values[i];
        sumXY += i * values[i];
        sumX2 += i * i;
    }
    const denominator = n * sumX2 - sumX * sumX;
    if (denominator === 0) {
        return { slope: 0, trend: 'flat' };
    }
    const slope = (n * sumXY - sumX * sumY) / denominator;
    // Valori costanti → slope esattamente 0 → sempre flat
    if (slope === 0) {
        return { slope: 0, trend: 'flat' };
    }
    // Calcola la deviazione standard per determinare la soglia di "flat"
    const mean = sumY / n;
    let sumSqDiff = 0;
    for (const v of values) {
        sumSqDiff += (v - mean) * (v - mean);
    }
    const stdDev = Math.sqrt(sumSqDiff / n);
    const threshold = stdDev * 0.1;
    if (Math.abs(slope) < threshold) {
        return { slope, trend: 'flat' };
    }
    if (direction === 'context') {
        return { slope, trend: 'flat' };
    }
    const isPositiveGood = direction === 'higher_better';
    const isImproving = isPositiveGood ? slope > 0 : slope < 0;
    return { slope, trend: isImproving ? 'improving' : 'declining' };
}
/**
 * Tabella delle conversioni unità supportate (spec sez. 10).
 * Chiave: "from_to", valore: fattore moltiplicativo.
 * kg↔lb e cm↔in usano le costanti UNICHE di `constants.ts` (censimento:
 * 3 costanti lb→kg diverse nella piattaforma — mai più).
 */
const UNIT_CONVERSIONS = {
    kg_lb: constants_1.KG_TO_LB,
    lb_kg: constants_1.LB_TO_KG,
    cm_in: constants_1.CM_TO_IN,
    in_cm: constants_1.IN_TO_CM,
    m_cm: 100,
    cm_m: 0.01,
    m_ft: 3.28084,
    ft_m: 0.3048,
    'km/h_mph': 0.62137,
    'mph_km/h': 1.60934,
};
/**
 * Converte un valore da un'unità a un'altra (spec sez. 10).
 * Solo per visualizzazione — il DB mantiene sempre l'unità originale.
 * Output arrotondato a 2 decimali (convenzione backend).
 * Ritorna null se la conversione non è supportata.
 */
function convertUnit(value, fromUnit, toUnit) {
    if (fromUnit === toUnit)
        return value;
    const key = `${fromUnit}_${toUnit}`;
    const factor = UNIT_CONVERSIONS[key];
    if (factor == null)
        return null;
    return Math.round(value * factor * 100) / 100;
}
/**
 * Normalizza un valore misurato all'unità canonica del test (la
 * `defaultUnit` della definizione) — stesso comportamento del backend
 * (testing.service, calcolo analytics): i valori del team vengono portati
 * tutti alla stessa unità PRIMA di media e percentile.
 *
 * - unità uguali o non note → valore invariato;
 * - conversione supportata → valore convertito;
 * - conversione NON supportata → null (il chiamante ESCLUDE il valore dal
 *   confronto, come fa il backend).
 *
 * Il FE storicamente saltava questo passaggio: team con risultati misti
 * kg/lb mostravano percentili web diversi da quelli del backend. Il fix è
 * VOLUTO (censimento R7).
 */
function normalizeTestValue(value, storedUnit, canonicalUnit) {
    if (!canonicalUnit || !storedUnit || storedUnit === canonicalUnit) {
        return value;
    }
    return convertUnit(value, storedUnit, canonicalUnit);
}
function calculateRecoveryPct(currentValue, preInjuryValue) {
    if (preInjuryValue == null || preInjuryValue <= 0)
        return null;
    const value = Math.round((currentValue / preInjuryValue) * 1000) / 10;
    let zone;
    if (value >= 90)
        zone = 'green';
    else if (value >= 80)
        zone = 'yellow';
    else
        zone = 'red';
    return { value, zone };
}
/**
 * Determina se un test RTP è superato in base al tipo di soglia (spec sez. 8.3).
 */
function isRTPCriteriaMet(thresholdType, thresholdValue, currentValue, lsiValue, preInjuryValue, direction) {
    switch (thresholdType) {
        case 'absolute': {
            // Per direction LOWER_BETTER (es. Tampa Scale ≤25): il valore deve essere <= soglia
            if (direction === 'lower_better') {
                return currentValue <= thresholdValue;
            }
            // Per HIGHER_BETTER (es. ACL-RSI ≥56): il valore deve essere >= soglia
            return currentValue >= thresholdValue;
        }
        case 'lsi':
            return lsiValue != null && lsiValue >= thresholdValue;
        case 'pre_injury_pct': {
            if (preInjuryValue == null || preInjuryValue <= 0)
                return false;
            const recoveryPct = (currentValue / preInjuryValue) * 100;
            return recoveryPct >= thresholdValue;
        }
        default:
            return false;
    }
}
// ═══════════════════════════════════════════════════════════════════
// Motore metriche DERIVED multi-metrica (spec Testing sez.4)
// "Tra le metriche configurate, alcune si inseriscono e altre si calcolano —
//  dipende dalla FONTE del dato." Se il coach inserisce i grezzi, le derivate
//  si popolano in automatico e diventano sola-lettura. Se inserisce direttamente
//  la derivata, si accetta il valore e i grezzi restano vuoti.
// ═══════════════════════════════════════════════════════════════════
/** Accelerazione di gravità (m/s²) per i calcoli impulse-momentum. */
exports.GRAVITY = 9.81;
/**
 * RSI (Reactive Strength Index) da tempo di volo e tempo di contatto (spec sez.4).
 * RSI = flight_time / contact_time (stessa unità → adimensionale).
 * @param flightTimeMs - tempo di volo (ms)
 * @param contactTimeMs - tempo di contatto al suolo (ms)
 * @returns RSI arrotondato a 3 decimali, o null se input non validi
 */
function rsiFromFlightContact(flightTimeMs, contactTimeMs) {
    if (!Number.isFinite(flightTimeMs) ||
        !Number.isFinite(contactTimeMs) ||
        contactTimeMs <= 0) {
        return null;
    }
    return Math.round((flightTimeMs / contactTimeMs) * 1000) / 1000;
}
/**
 * Altezza di salto dal tempo di volo (metodo impulse-momentum, spec sez.4).
 * h = g · t² / 8  (t = tempo di volo in secondi). Ritorna cm.
 * @param flightTimeMs - tempo di volo (ms)
 * @returns altezza in cm arrotondata a 1 decimale, o null se input non valido
 */
function jumpHeightFromFlightTime(flightTimeMs) {
    if (!Number.isFinite(flightTimeMs) || flightTimeMs <= 0)
        return null;
    const t = flightTimeMs / 1000; // ms → s
    const heightM = (exports.GRAVITY * t * t) / 8;
    return Math.round(heightM * 100 * 10) / 10; // m → cm, 1 decimale
}
/**
 * Registro delle metriche derivate note (estendibile per nuovi protocolli).
 * L'ordine conta: derivate che dipendono da altre derivate vanno dopo.
 */
exports.DERIVED_METRICS = [
    {
        key: 'rsi',
        inputs: ['flight_time_ms', 'contact_time_ms'],
        compute: (v) => rsiFromFlightContact(v.flight_time_ms, v.contact_time_ms),
    },
    {
        key: 'height_cm',
        inputs: ['flight_time_ms'],
        compute: (v) => jumpHeightFromFlightTime(v.flight_time_ms),
    },
];
/**
 * Applica le regole derived su una mappa di valori (spec sez.4):
 *  - se i grezzi richiesti sono presenti E la derivata NON è stata inserita
 *    direttamente → calcola la derivata e marcala come sola-lettura;
 *  - se la derivata è già stata inserita direttamente → la si rispetta (no overwrite);
 *  - i grezzi mancanti non bloccano (salvataggio parziale).
 *
 * @param input - valori inseriti dal coach (grezzi e/o derivate)
 * @param defs - definizioni derivate (default: DERIVED_METRICS)
 * @returns valori arricchiti + elenco delle chiavi derivate auto-popolate
 */
function applyDerivedMetrics(input, defs = exports.DERIVED_METRICS) {
    const values = { ...input };
    const derivedFields = [];
    for (const def of defs) {
        const alreadyProvided = input[def.key] !== undefined && input[def.key] !== null;
        if (alreadyProvided)
            continue; // derivata inserita direttamente → rispetta
        const hasAllInputs = def.inputs.every((k) => values[k] !== undefined && values[k] !== null);
        if (!hasAllInputs)
            continue; // grezzi insufficienti → salta (parziale)
        const computed = def.compute(values);
        if (computed === null || computed === undefined)
            continue;
        values[def.key] = computed;
        derivedFields.push(def.key);
    }
    return { values, derivedFields };
}
